import * as React from 'react';
import { Button, Checkbox, Collapse, InputNumber, Modal, Space, Typography, message } from 'antd';
import UAParser from 'ua-parser-js';
import versionData from '../../../build.json';
import { getFeatureFlag } from '../../Service/FeatureFlags';
import { getToken } from '../../Service/AuthService';
import { isValidCoordinates, minimumAccuracy, TWPositionData, watchHistory } from '../../Service/GeolocationService';
import UTCDateFormatter from '../../Helper/DateFormatter/UTCDateFomatter';
import { LOG_SENT, LOG_SHARE_FALLBACK, SENDING_LOG, logNotSent } from '../../Helper/BannerMessages';


// Reading the logs is a Test-build aid: `ENABLE_DEBUG` is true in `.env.test` only, and a
// `?flags=debug,true` query can turn the two viewers on elsewhere for a single visit.
const LOG_VIEWERS_ENABLED = getFeatureFlag('debug') === true;

// The anonymous drop share where the field logs are collected (Nextcloud, upload only).
export const SHARE_LOG_URL = 'https://cloud.sustainablethings.eu/index.php/s/RzR2KrFCE26XqsC';

// `TreeWallet App <v> | WebView Chrome <v> | <model> - Android <v> - API <n> | GNSS raw capable: …`
const SHELL_USER_AGENT = /^TreeWallet App (.+?) \| WebView (.+?) \| (.+?) - Android (.+?) - API (\d+)/;


const { Panel } = Collapse;
const { Text } = Typography;

interface DebugDataProps{
  data?: any;
  coordinates?: any;
  onAccuracyUpdate?: Function;
}

interface DebugDataState{
  accuracy?: {
    enabled?: boolean;
    value?: number|null|undefined;
  }
  availableStorageGb?: number|null;
  persistentStorage?: boolean|null;
  logging?: boolean;
  rawSupported?: boolean;
  // The log on show, already read from the shell: `null` closes the viewer.
  logView?: { title: string; text: string } | null;
}

const LOG_TEXT_STYLE: React.CSSProperties = {
  margin: 0,
  color: '#fff',
  backgroundColor: '#000',
  fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
  fontSize: '12px',
  lineHeight: '1.45',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

/**
 * The page's own identity, reduced from the user agent in three lines: the app build, the engine,
 * and the device with its system. The shell writes its own block first; this is what the page sees.
 */
export function describeWebUserAgent(): string[] {
  const userAgent = navigator.userAgent;
  const build = (versionData as any)?.build_number || (versionData as any)?.version
    || (versionData as any)?.date || 'dev';
  // Installed to the home screen rather than opened in a browser tab: iOS answers `navigator.standalone`.
  const displayModes = ['standalone', 'minimal-ui', 'fullscreen'];
  const installed = (typeof window.matchMedia === 'function'
    && displayModes.some((mode) => window.matchMedia(`(display-mode: ${mode})`).matches))
    || (navigator as any).standalone === true;
  const lines = [`TreeWallet ${installed ? 'PWA' : 'Web-App'} ${build}`];

  // The Android shell logs its own block first - TreeWallet App, WebView Chrome, model - Android - API,
  // GNSS raw capable - so only the web bundle's build is left to record here.
  const shell = SHELL_USER_AGENT.exec(userAgent);
  if (shell) {
    return lines;
  }

  const { browser, os, device } = new UAParser(userAgent).getResult();
  const engine = [browser.name?.replace(/\s*WebView$/, ''), browser.version].filter(Boolean).join(' ') || 'unknown';
  const system = [os.name, os.version].filter(Boolean).join(' ') || 'unknown system';
  const model = [device.vendor, device.model].filter(Boolean).join(' ') || 'unknown device';

  lines.push(`WebView ${engine}`, `${model} - ${system}`);
  return lines;
}

/**
 * What a support log has to contain: the shell's own file when the page can read it, and otherwise the
 * errors the app has been collecting (`onError` in src/index.tsx).
 */
function collectedLogBody(): string {
  const shellLog = window.TwGnssRaw?.readAppLog?.();
  if (typeof shellLog === 'string' && shellLog.trim() !== '') {
    return shellLog;
  }

  return localStorage.getItem('errors') || '(no error recorded)';
}

/**
 * Sends the log to us: the page posts it to `/api/log/share`, which puts it on the drop share. It cannot
 * go there directly - Nextcloud sends no CORS header, and a form cannot send the `PUT` it asks for - so
 * the fallback is the share page, where the file is dropped by hand.
 */
export async function shareLogs() {
  const hideLoading = message.loading(SENDING_LOG, 0);
  const kind = window.TwGnssRaw ? 'app' : 'web';
  const stamp = new Date().toISOString().replace(/[:.]/g, '-');
  const name = `treewallet-${kind}-${stamp}.log`;
  const content = describeWebUserAgent().concat([''], [collectedLogBody()]).join('\n');

  try {
    const response = await fetch(`${process.env.BASE_API_URL}/api/log/share`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
      },
      body: JSON.stringify({ name, content }),
    });
    const answer = await response.json().catch(() => ({}));

    hideLoading();

    if (!response.ok) {
      message.error(answer.message || logNotSent(response.status));
      return;
    }

    message.success(answer.message || LOG_SENT);
  } catch (error) {
    hideLoading();
    console.warn('TreeWallet: the log could not be sent to the API', error);
    message.warning(LOG_SHARE_FALLBACK);
    window.open(SHARE_LOG_URL, '_blank');
  }
}

/** The viewer's own button: one tap sends both logs, from the popup showing them. */
export function ShareLogButton() {
  return (
    <Button size="small" onClick={shareLogs}>
      Share Log
    </Button>
  );
}

export default class DebugData extends React.Component<DebugDataProps, DebugDataState>{
  private logViewerRef = React.createRef<HTMLPreElement>();

  constructor(props: DebugDataProps) {
    super(props);
    this.state = {
      accuracy: {
        enabled: false,
        value: undefined,
      },
    }
  }

  componentDidMount() {
    this.refreshAvailableStorage();
    this.refreshPersistentStorage();
    this.refreshLoggingState();
    // Three lines the shell copies into the app log, next to the block it writes itself.
    console.info(describeWebUserAgent().join('\n'));
  }

  // The newest lines are the interesting ones: the viewer opens at the bottom.
  componentDidUpdate(_props: DebugDataProps, previous: DebugDataState) {
    if (!previous.logView && this.state.logView) {
      setTimeout(() => {
        const container = this.logViewerRef.current?.parentElement;
        if (container) container.scrollTop = container.scrollHeight;
      }, 0);
    }
  }

  // The bridge exists only in the Android Test build, so the button stays hidden everywhere else.
  refreshLoggingState() {
    if (window.TwGnssRaw) {
      this.setState({
        logging: window.TwGnssRaw.isRunning(),
        rawSupported: window.TwGnssRaw.isSupported(),
      });
      return;
    }

    this.setState({ rawSupported: false });
  }

  toggleLogging() {
    const bridge = window.TwGnssRaw;
    if (!bridge) {
      return;
    }

    if (this.state.logging) {
      bridge.stop();
    } else {
      bridge.start();
    }
    this.setState({ logging: !this.state.logging });
  }

  // Reads the tail of one of the on-device logs; nothing here ever writes to them.
  openLog(kind: 'app' | 'gnss') {
    const bridge = window.TwGnssRaw;
    let text = 'This build cannot read the log from the page.';

    try {
      const tail = kind === 'app' ? bridge?.readAppLog?.() : bridge?.readGnssLog?.();
      if (typeof tail === 'string') text = tail;
    } catch (error) {
      text = 'The log could not be read: ' + error;
    }

    this.setState({ logView: { title: kind === 'app' ? 'App log' : 'GNSS raw log', text } });
  }

  // Red when the platform has no raw GNSS (old Android device for instance): 
  // Yellow when the phone can provide raw GNSS measurements
  renderRawSupport() {
    const { rawSupported } = this.state;
    const style: any = {
      display: 'inline-block',
      padding: '4px 10px',
      border: '2px solid #000',
      backgroundColor: rawSupported ? '#f7f3a0' : '#c22f1f',
      color: '#000',
      fontWeight: 'bold',
      textAlign: 'center',
      lineHeight: '1.2',
      verticalAlign: 'middle',
    };

    return (
      <span style={style}>
        Raw GNSS
        <br />
        {rawSupported ? 'available' : 'not available'}
      </span>
    );
  }

  // The space the app may still fill, in GB, as the browser reports it: `quota` is the share of the device
  // this origin is allowed to use - the storage of photos in the queue is what fills it - and `usage`
  // what is already taken. A page is not given the device's free memory on iOS or Android, so this is
  // the figure that has a meaning here. `estimate()` answers asynchronously and is a snapshot, so it is
  // read once, when this panel is mounted with the form, and the value kept in the state.
  async refreshAvailableStorage() {
    if (!navigator.storage?.estimate) {
      this.setState({ availableStorageGb: null });
      return;
    }

    try {
      const { quota, usage } = await navigator.storage.estimate();
      this.setState({ availableStorageGb: ((quota || 0) - (usage || 0)) / (1024 * 1024 * 1024) });
    } catch (error) {
      console.warn('Could not read the storage estimate:', error);
      this.setState({ availableStorageGb: null });
    }
  }

  // Whether the browser has put this site in its "persistent" bucket, that is whether it promised not to
  // evict the stored trees to reclaim space when the device runs short - the queue is evicted without
  // warning otherwise, even below the quota. `persisted()` is not implemented by every browser (Safari
  // has no `persist()` either), hence the `null` shown as "unavailable" rather than as "not granted".
  async refreshPersistentStorage() {
    if (!navigator.storage?.persisted) {
      this.setState({ persistentStorage: null });
      return;
    }

    try {
      this.setState({ persistentStorage: await navigator.storage.persisted() });
    } catch (error) {
      console.warn('Could not read the persistent storage state:', error);
      this.setState({ persistentStorage: null });
    }
  }

  // Asking again from a click, because that is the only way the answer can become "granted" in most
  // browsers: Firefox shows its permission doorhanger only for a call made during a user gesture, and
  // Chromium decides silently through its own heuristics - an installed web app, a bookmark, an
  // engagement score - so the call made at start up in `src/index.tsx` is refused with no prompt and no
  // explanation. The line above is read again afterwards, so the answer is visible where it was asked.
  async requestPersistentStorage() {
    if (!navigator.storage?.persist) return;

    try {
      const granted = await navigator.storage.persist();
      console.debug('TreeWallet: persistent storage request answered', granted ? 'granted' : 'not granted');
    } catch (error) {
      console.warn('Could not request persistent storage:', error);
    }

    this.refreshPersistentStorage();
  }

  // One block per position, newest first: a header naming it - "[Newest position]" for the first one,
  // then "[2]", "[3]" ... up to "[N]" - its UTC time, then the values as delivered, except the
  // accuracy, which is shown with 2 decimals to stay readable next to its unit.
  // The label is the rank in the list, not the age of the fix: the history receives the same cached fix
  // more than once (the one second polling asks with `maximumAge`), and two fixes of the same second
  // used to be labelled "[Newest position]" twice. A fix which is 10 seconds old or more - where the
  // 10-slot history (`watchHistory.maxValues`) would have dropped it - is not shown, so at most 10
  // blocks are listed.
  renderDebugAllDataItems(debugData: TWPositionData[]) {
    if (debugData.length === 0) {
      return (
        <div>
          <Text>No data yet</Text>
        </div>
      );
    }

    const headerStyle = { marginRight: '8px' };
    const valueStyle = { marginLeft: '5px', marginRight: '12px' };
    const newestTimestamp = debugData[0].timestamp || 0;

    // Only the positions of the last 10 seconds are displayed: the older ones are the ones the 10-slot
    // history is about to drop.
    const positions = debugData.filter((coords: TWPositionData) => {
      const secondsAgo = Math.round((newestTimestamp - (coords.timestamp || newestTimestamp)) / 1000);
      return secondsAgo < watchHistory.maxValues;
    });

    return positions.map((coords: TWPositionData, index: number) => {
      const time = coords.timestamp ? UTCDateFormatter.formatDateFromTimestamp(coords.timestamp).time : '';
      // The raw accuracy carries up to ~15 digits, while 2 are enough to compare the last positions.
      const accuracy = coords.accuracy || 0;
      const accuracyText = `${accuracy.toFixed(2)}m`;

      return (
        <div key={`debugdata-${coords.timestamp}`} style={{ marginBottom: '8px' }}>
          <div>
            <Text mark style={headerStyle}>{index === 0 ? '[Newest position]' : `[${index + 1}]`}</Text>
            <Text mark>{time} (UTC)</Text>
          </div>
          {isValidCoordinates(coords) ? (
            <div>
              <Text strong>Acc</Text><Text style={valueStyle}>{accuracyText}</Text>
              <Text strong>Lat</Text><Text style={valueStyle}>{coords.lat}</Text>
              <Text strong>Lng</Text><Text style={valueStyle}>{coords.lng}</Text>
            </div>
          ) : (
            <Text type="danger">Invalid coordinates</Text>
          )}
        </div>
      );
    })
  }

  // `undefined` while the first estimate is in flight, `null` when the browser does not report one.
  renderAvailableStorage() {
    const { availableStorageGb } = this.state;

    if (availableStorageGb === undefined) {
      return <Text>...</Text>;
    }

    if (availableStorageGb === null) {
      return <Text>unavailable</Text>;
    }

    return <Text>{`${availableStorageGb.toFixed(2)} GB`}</Text>;
  }

  renderPersistentStorage() {
    const { persistentStorage } = this.state;

    if (persistentStorage === undefined) {
      return <Text>...</Text>;
    }

    if (persistentStorage === null) {
      return <Text>unavailable</Text>;
    }

    return <Text>{persistentStorage ? 'granted' : 'not granted'}</Text>;
  }

  renderControls() {
    const { onAccuracyUpdate } = this.props;
    const { accuracy } = this.state;

    const updateAccuracy = (updatedAccuracy: number|null|undefined, enabled: boolean) => {
      this.setState({
        accuracy: { enabled, value: updatedAccuracy }
      });
      onAccuracyUpdate && onAccuracyUpdate(updatedAccuracy);
    };

    return (
      <div>
        <Space size={"middle"}>
          <InputNumber min={1} max={9999} defaultValue={minimumAccuracy} onChange={(e) => updateAccuracy(
            e ? e : 0,
            accuracy?.enabled ? true : false
          )}/>
        </Space>
        <Space>
          <Checkbox
            onChange={(e) => updateAccuracy(
              accuracy?.value,
              e.target.checked
            )}
            defaultChecked={accuracy?.enabled}
          >
          </Checkbox>
        </Space>
      </div>
    );
  }

  renderLogViewer() {
    const { logView } = this.state;
    if (!logView) {
      return null;
    }

    return (
      <Modal
        visible
        title={logView.title}
        footer={<div style={{ textAlign: 'center' }}><ShareLogButton /></div>}
        width="100%"
        style={{ top: 0, maxWidth: '100%', margin: 0, paddingBottom: 0, borderRadius: 0 }}
        bodyStyle={{ height: 'calc(100vh - 108px)', margin: 0, overflow: 'auto', backgroundColor: '#000' }}
        onCancel={() => this.setState({ logView: null })}
      >
        <pre ref={this.logViewerRef} style={LOG_TEXT_STYLE}>{logView.text}</pre>
      </Modal>
    );
  }

  render() {
    // A copy, newest first. `watchHistory.values` is shared with the service, and `add` keeps the last
    // `maxValues` *by position*: sorting it in place would put the newest first, so the next fix -
    // pushed at the end - would make it evict the newest instead of the oldest. It would also reorder
    // the `gpsHistory` the tree payload is built from.
    // A position without `timestamp` (an error entry) is sorted as the oldest, so the first block of
    // the list is always the newest fix.
    const data: TWPositionData[] = [...watchHistory.values]
      .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));

    return (
      <Collapse ghost>
        <Panel header={<Text code>debug gnss ({data.length})</Text>} key={1}>
          {/* The coordinates rows are adjacent inline elements without spaces,
              so they need to be breakable to stay readable on narrow screens. */}
          <div style={{ overflowWrap: 'anywhere' }}>
            <div style={{ marginBottom: '10px', display: 'none' }}>{this.renderControls()}</div>
            <div style={{ marginBottom: '10px' }}>
              {this.renderRawSupport()}
              {window.TwGnssRaw && (
                <>
                  <Button
                    size="small"
                    style={{ marginLeft: '8px' }}
                    danger={this.state.logging}
                    // Stoppable even when the platform reports no support: a running session must
                    // never become unkillable.
                    disabled={!this.state.rawSupported && !this.state.logging}
                    onClick={() => this.toggleLogging()}
                  >
                    {this.state.logging ? 'Stop GNSS Logging' : 'Start GNSS Logging'}
                  </Button>
                  {LOG_VIEWERS_ENABLED && (
                    <>
                      <Button size="small" style={{ marginLeft: '8px' }} onClick={() => this.openLog('app')}>
                        View App Log
                      </Button>
                      <Button size="small" style={{ marginLeft: '8px' }} onClick={() => this.openLog('gnss')}>
                        View GNSS Raw
                      </Button>
                    </>
                  )}
                </>
              )}
            </div>
            <div>
              <div>
                <Text strong>Memory space available = </Text>
                {this.renderAvailableStorage()}
                <Button
                  size="small"
                  style={{ marginLeft: '8px' }}
                  disabled={this.state.availableStorageGb === null}
                  onClick={() => this.refreshAvailableStorage()}
                >
                  Refresh
                </Button>
              </div>
              <div>
                <Text strong>Persistent storage = </Text>
                {this.renderPersistentStorage()}
                <Button
                  size="small"
                  style={{ marginLeft: '8px' }}
                  disabled={this.state.persistentStorage === null}
                  onClick={() => this.requestPersistentStorage()}
                >
                  Request
                </Button>
              </div>
              <div><Text strong>Last 10 positions</Text></div>
              {this.renderDebugAllDataItems(data)}
            </div>
            {this.renderLogViewer()}
          </div>
        </Panel>
      </Collapse>
    );
  }
}
